import { FormEvent, useEffect, useState } from 'react';
import { ApiError } from '../../services/api';
import {
  dealerErrorCode, dealerErrorFields, dealerErrorMessage, dealersApi, isFeatureOff, needsFinanceMessage,
} from '../../services/dealersApi';
import { CreateDealerRequest, Dealer, UpdateDealerRequest } from '../../types/dealers';
import { describedBy, FormField } from '../promotions/PromoFormParts';
import BreakEvenHint from './BreakEvenHint';
import { discountError, sameAmount } from './dealerFormat';

interface Values {
  legalName: string;
  tradeName: string;
  trn: string;
  billingEmail: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  discountPct: string;
  paymentTermsDays: string;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Loose: orders normalises the number and answers 422 INVALID_PHONE on the field. */
const PHONE = /^\+?[\d\s-]{8,20}$/;

function initial(d: Dealer | null): Values {
  return {
    legalName: d?.legalName ?? '',
    tradeName: d?.tradeName ?? '',
    trn: d?.trn ?? '',
    billingEmail: d?.billingEmail ?? '',
    contactName: d?.contact.name ?? '',
    contactPhone: d?.contact.phone ?? '',
    contactEmail: d?.contact.email ?? '',
    discountPct: d ? String(Number(d.discountPct)) : '0',
    paymentTermsDays: d?.paymentTermsDays != null ? String(d.paymentTermsDays) : '',
  };
}

/** Client checks matching `DealerCreditRequests.CreateDealer` and `DealerService` (terms 0–365). */
export function validateDealer(v: Values): Record<string, string> {
  const e: Record<string, string> = {};
  if (!v.legalName.trim()) e.legalName = 'Enter the legal name';
  if (!v.tradeName.trim()) e.tradeName = 'Enter the trade name';
  if (v.trn.trim().length > 15) e.trn = 'At most 15 characters';
  if (!EMAIL.test(v.billingEmail.trim())) e.billingEmail = 'Enter a valid email';
  if (!v.contactName.trim()) e['contact.name'] = 'Enter the contact name';
  if (!PHONE.test(v.contactPhone.trim())) e['contact.phone'] = 'Enter a valid mobile number, e.g. +971501234567';
  if (v.contactEmail.trim() && !EMAIL.test(v.contactEmail.trim())) e['contact.email'] = 'Enter a valid email';
  const d = discountError(v.discountPct);
  if (d) e.discountPct = d;
  const t = v.paymentTermsDays.trim();
  if (t && (!/^\d+$/.test(t) || Number(t) > 365)) e.paymentTermsDays = 'Whole days, 0–365';
  return e;
}

function toCreate(v: Values): CreateDealerRequest {
  return {
    legalName: v.legalName.trim(),
    tradeName: v.tradeName.trim(),
    trn: v.trn.trim() || null,
    billingEmail: v.billingEmail.trim(),
    contact: { name: v.contactName.trim(), phone: v.contactPhone.trim(), email: v.contactEmail.trim() || null },
    discountPct: v.discountPct.trim(),
    paymentTermsDays: v.paymentTermsDays.trim() ? Number(v.paymentTermsDays.trim()) : null,
  };
}

/** PATCH with only what changed (null = unchanged server-side); the contact goes as a whole. */
function toUpdate(v: Values, d: Dealer): UpdateDealerRequest {
  const was = initial(d);
  const out: UpdateDealerRequest = {};
  if (v.legalName.trim() !== was.legalName) out.legalName = v.legalName.trim();
  if (v.tradeName.trim() !== was.tradeName) out.tradeName = v.tradeName.trim();
  if (v.trn.trim() !== was.trn) out.trn = v.trn.trim();
  if (v.billingEmail.trim() !== was.billingEmail) out.billingEmail = v.billingEmail.trim();
  if (v.contactName.trim() !== was.contactName || v.contactPhone.trim() !== was.contactPhone || v.contactEmail.trim() !== was.contactEmail) {
    out.contact = { name: v.contactName.trim(), phone: v.contactPhone.trim(), email: v.contactEmail.trim() || null };
  }
  if (!sameAmount(v.discountPct, d.discountPct)) out.discountPct = v.discountPct.trim();
  if (v.paymentTermsDays.trim() && v.paymentTermsDays.trim() !== was.paymentTermsDays) out.paymentTermsDays = Number(v.paymentTermsDays.trim());
  return out;
}

interface DealerFormProps {
  /** null = New dealer (DRAFT); otherwise the dealer to edit. */
  dealer: Dealer | null;
  token: string;
  /** The admin holds finance-config (or is super admin): a discount at or above break-even saves. */
  canSaveAboveBreakEven: boolean;
  onClose: () => void;
  onSaved: (saved: Dealer) => void;
  onFeatureOff: () => void;
}

/**
 * Spec 085 Flow A / E — create or edit a dealer. The discount field carries the break-even hint
 * (BR-36); a 403 `DISCOUNT_ABOVE_BREAK_EVEN_NEEDS_FINANCE` lands on that field.
 */
export default function DealerForm({ dealer, token, canSaveAboveBreakEven, onClose, onSaved, onFeatureOff }: DealerFormProps) {
  const editing = dealer !== null;
  const [v, setV] = useState<Values>(() => initial(dealer));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [lowest, setLowest] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let live = true;
    dealersApi.breakEven(token)
      .then((b) => { if (live) setLowest(b.lowestCommissionPct ?? null); })
      // Best-effort, like the server's hint: no rate shown, the server still enforces the rule.
      .catch(() => { if (live) setLowest(null); });
    return () => { live = false; };
  }, [token]);

  const set = <K extends keyof Values>(key: K, value: Values[K]) => {
    setV((prev) => ({ ...prev, [key]: value }));
    const field = key.startsWith('contact') ? `contact.${key.slice(7).toLowerCase()}` : key;
    setErrors((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setFormError(null);
    const local = validateDealer(v);
    if (Object.keys(local).length) { setErrors(local); return; }
    setErrors({});
    let body: CreateDealerRequest | UpdateDealerRequest;
    if (dealer) {
      body = toUpdate(v, dealer);
      if (Object.keys(body).length === 0) { onClose(); return; }
    } else {
      body = toCreate(v);
    }
    setSaving(true);
    try {
      const saved = dealer
        ? await dealersApi.update(dealer.id, body, token)
        : await dealersApi.create(body as CreateDealerRequest, token);
      onSaved(saved);
    } catch (err) {
      if (isFeatureOff(err)) { onFeatureOff(); onClose(); return; }
      if (dealerErrorCode(err) === 'DISCOUNT_ABOVE_BREAK_EVEN_NEEDS_FINANCE') {
        setErrors({ discountPct: needsFinanceMessage(err) });
        return;
      }
      const fields = dealerErrorFields(err);
      if (err instanceof ApiError && (err.status === 400 || err.status === 422) && Object.keys(fields).length) {
        setErrors(fields);
        setFormError('Fix the fields marked below.');
      } else {
        setFormError(dealerErrorMessage(err, 'Failed to save the dealer'));
      }
    } finally {
      setSaving(false);
    }
  };

  const text = (key: keyof Values, field: string, label: string, opts: { type?: string; maxLength?: number; hint?: string } = {}) => (
    <FormField id={`dealer-${field}`} label={label} error={errors[field]} hint={opts.hint}>
      <input id={`dealer-${field}`} className="admin-form-input" type={opts.type ?? 'text'} value={v[key]} maxLength={opts.maxLength}
        disabled={saving} {...describedBy(`dealer-${field}`, errors[field], !!opts.hint)} onChange={(e) => set(key, e.target.value)} />
    </FormField>
  );

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" role="dialog" aria-modal="true" aria-label={editing ? `Edit ${dealer.tradeName}` : 'New dealer'} style={{ maxWidth: '640px', width: '100%' }}>
        <div className="admin-modal-header">
          <h3 style={{ margin: 0 }}>{editing ? `Edit ${dealer.tradeName}` : 'New dealer'}</h3>
          <button type="button" className="admin-modal-close" onClick={onClose} disabled={saving} aria-label="Close">&times;</button>
        </div>
        <form onSubmit={submit} noValidate>
          <div className="admin-modal-body">
            {formError && <div className="admin-info-banner danger" role="alert" data-testid="dealer-form-error">{formError}</div>}
            {!editing && (
              <div className="admin-info-banner info" role="note">
                The dealer starts as a draft. Add users, showrooms and the signed agreement, then activate it.
              </div>
            )}
            <div className="dealer-form-grid">
              {text('legalName', 'legalName', 'Legal name', { maxLength: 200 })}
              {text('tradeName', 'tradeName', 'Trade name', { maxLength: 120, hint: 'Shown to buyers next to their credit.' })}
              {text('trn', 'trn', 'TRN (optional)', { maxLength: 15 })}
              {text('billingEmail', 'billingEmail', 'Billing email', { type: 'email', maxLength: 254, hint: 'Proformas and invoices go here.' })}
            </div>
            <h4 className="dealer-form-section">Registered contact</h4>
            <div className="dealer-form-grid">
              {text('contactName', 'contact.name', 'Name', { maxLength: 120 })}
              {text('contactPhone', 'contact.phone', 'Phone', { type: 'tel', maxLength: 32, hint: 'International format, e.g. +971501234567.' })}
              {text('contactEmail', 'contact.email', 'Email (optional)', { type: 'email', maxLength: 254 })}
            </div>
            <h4 className="dealer-form-section">Terms</h4>
            <div className="dealer-form-grid">
              <FormField id="dealer-discountPct" label="Dealer discount (%)" error={errors.discountPct}
                hint="The dealer's whole cut on per-buyer credit. A pack's discount wins when it is higher; they never add up.">
                <input id="dealer-discountPct" className="admin-form-input" inputMode="decimal" value={v.discountPct} disabled={saving}
                  {...describedBy('dealer-discountPct', errors.discountPct, true)} onChange={(e) => set('discountPct', e.target.value)} />
              </FormField>
              {text('paymentTermsDays', 'paymentTermsDays', 'Payment terms (days, optional)', { hint: 'Leave blank to use the global auto-revoke days.' })}
            </div>
            <BreakEvenHint
              discountPct={v.discountPct}
              lowestCommissionPct={lowest}
              saved={dealer?.breakEven ?? null}
              canSaveAboveBreakEven={canSaveAboveBreakEven}
            />
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn admin-btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create dealer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
